<template>
  <div class="filter-sidebar upcoming-events-filter">
    <modal
      ref="filterSidebar"
      variant="drawer"
      custom-footer
      @input="handleOpenModal"
      @close="handleCloseModal"
    >
      <template #default="{ activate, isActive }">
        <slot v-bind="{ activate, isActive, resetAllFilters, totalFiltersSelected }" />
      </template>

      <template #header>
        <div class="filter-sidebar-title">
          <span>{{ dictionary['upcoming-events:filters'] }}</span>
        </div>
      </template>
      <template #body>
        <div class="form">
          <div class="filter-sidebar-items">
            <div
              v-if="showTypesFilter"
              class="filter-sidebar-item filter-sidebar-item--types"
            >
              <div class="filter-sidebar-item-header">
                <div>
                  <span class="filter-sidebar-item-title">{{ dictionary['upcoming-events:type'] }}</span>
                  <button
                    v-if="selectedTypes.length > 0"
                    class="filter-sidebar-item-reset"
                    @click.stop="selectedTypes = []"
                  >
                    {{ dictionary['upcoming-events:reset'] }}
                  </button>
                </div>
                <span
                  v-if="selectedTypes.length > 0"
                  class="filter-sidebar-item-status"
                >{{ selectedTypes.length }}</span>
              </div>
              <div class="filter-sidebar-item-body">
                <ul class="filter-sidebar-item-list">
                  <li
                    v-for="item in eventTypes"
                    :key="item.name"
                    class="input checkbox"
                  >
                    <label
                      :for="item.name"
                      :class="{ disabled: typeIsDisabled(item.name) }"
                    >
                      <input
                        :id="item.name"
                        v-model="selectedTypes"
                        type="checkbox"
                        data-field-type="CheckBoxSet"
                        :value="item.name"
                        :disabled="typeIsDisabled(item.name)"
                      >
                      {{ item.name }}
                    </label>
                  </li>
                </ul>
              </div>
            </div>
            <div
              v-if="showFanExclusiveFilter"
              class="filter-sidebar-item filter-sidebar-item--fan-exclusive"
            >
              <label>
                <input
                  id="fan_exlusive"
                  v-model="isFanExclusiveSelected"
                  data-field-type="CheckBoxSet"
                  name="fan_exlusive"
                  type="checkbox"
                  :aria-label="dictionary['upcoming-events:fans-of-mo-exclusive']"
                  :disabled="fanExlusiveIsDisabled()"
                >
                {{ dictionary['upcoming-events:fans-of-mo-exclusive'] }}
              </label>
            </div>
            <div
              v-if="showComplimentaryFilter"
              class="filter-sidebar-item filter-sidebar-item--complimentary"
            >
              <label>
                <input
                  id="complimentary_filter"
                  v-model="isComplimentarySelected"
                  data-field-type="CheckBoxSet"
                  name="complimentary"
                  type="checkbox"
                  :aria-label="dictionary['upcoming-events:complimentary']"
                  :disabled="complimentaryIsDisabled()"
                >
                {{ dictionary['upcoming-events:complimentary'] }}
              </label>
            </div>
          </div>
        </div>
      </template>
      <template #footer="{ deactivate }">
        <div class="filter-sidebar-footer">
          <div class="filter-sidebar-footer-results">
            <div class="filter-sidebar-footer-results-text">
              {{ filteredEvents.length }} {{ dictionary['upcoming-events:matching-results'] }}
            </div>
            <button
              v-if="hasFilterSelected"
              class="filter-sidebar-footer-results-reset"
              @click="resetAllFilters"
            >
              {{ dictionary['upcoming-events:reset-all'] }}
            </button>
          </div>
          <div class="filter-sidebar-footer-cta">
            <button
              data-category="filter-apply"
              :data-filters="allSelectedFilters"
              @click="deactivate"
            >
              {{ dictionary['upcoming-events:apply'] }}
            </button>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
  import moment from 'moment/moment'
  import { mapState } from 'vuex'

  export default {
    name: 'UpcomingEventsFilter',
    props: {
      events: {
        type: Array,
        required: true,
        default: () => ([])
      },
      settings: {
        type: Object,
        required: true,
        default: () => ({})
      },
      dictionary: {
        type: Object,
        required: true,
        default: () => ({})
      },
      dateRange: {
        type: Object,
        default: () => ({})
      }
    },
    data() {
      return {
        isOpen: false,
        availableTypes: [],
        selectedTypes: [],
        filteredEvents: [],
        isFanExclusiveSelected: false,
        isComplimentarySelected: false
      }
    },
    watch: {
      selectedTypes() {
        this.filterEvents()
      },
      isFanExclusiveSelected() {
        this.filterEvents()
      },
      isComplimentarySelected() {
        this.filterEvents()
      },
      dateRange() {
        this.filterEvents()
      },
    },
    mounted() {
      this.filterEvents()
    },
    computed: {
      ...mapState('responsive', { wWidth: 'width', wHeight: 'height' }),

      eventTypes() {
        return this.getAvailableTypes(this.events)
      },

      totalFiltersSelected() {
        let total = 0
        total = this.selectedTypes.length
          + (this.isFanExclusiveSelected ? 1 : 0)
          + (this.isComplimentarySelected ? 1 : 0)
        return total
      },

      hasFilterSelected() {
        return this.selectedTypes.length > 0
          || this.isFanExclusiveSelected
          || this.isComplimentarySelected
      },

      isDateRangeSelected() {
        return !!(this.dateRange?.selectedStartDate && this.dateRange?.selectedEndDate)
      },

      showFanExclusiveFilter() {
        return this.events?.some((item) => item.isFansOfMOExclusive) && this.settings.showFanOfMOFilter
      },

      showTypesFilter() {
        return this.events?.some((item) => item.eventTypes && item.eventTypes?.name) && this.settings.showEventTypeFilter
      },

      showComplimentaryFilter() {
        return this.events?.some((item) => item.isComplimentary) && this.settings.showComplimentaryFilter
      },

      allSelectedFilters() {
        return [
          ...this.selectedTypes,
          ...(this.isFanExclusiveSelected ? [this.dictionary['upcoming-events:fans-of-mo-exclusive']] : []),
          ...(this.isComplimentarySelected ? [this.dictionary['upcoming-events:complimentary']] : [])
        ].join('|')
      }
    },
    methods: {
      resetAllFilters() {
        this.isFanExclusiveSelected = false
        this.isComplimentarySelected = false
        this.selectedTypes = []
      },

      handleCloseModal() {
        document.body.classList.remove('no-scroll')
        this.isOpen = false
      },

      handleOpenModal() {
        document.body.classList.add('no-scroll')
        this.isOpen = true
      },

      getAvailableTypes(items) {
        const uniqueTypesMap = {}

        items.forEach((item) => {
          if (item.eventTypes.name) {
            uniqueTypesMap[item.eventTypes.id] = item.eventTypes
          }
        })
        return Object.values(uniqueTypesMap)
      },

      updateAvailableFilters() {
        const withoutTypeFilter = this.getFilteredEvents(this.events, 'eventTypes')
        this.availableTypes = this.getAvailableTypes(withoutTypeFilter)
      },

      getFilteredEvents(events, skipFilter = '') {
        return events.filter((item) => {
          if (this.isFanExclusiveSelected && !item.isFansOfMOExclusive) return false
          if (this.isComplimentarySelected && !item.isComplimentary) return false

          if (this.selectedTypes.length > 0 && skipFilter !== 'eventTypes') {
            const isTypeMatch = this.selectedTypes.some((selectedtype) => selectedtype === item.eventTypes.name)

            if (!isTypeMatch) {
              return false
            }
          }
          if (this.isDateRangeSelected && skipFilter !== 'dateRange') {
            const itemStartDate = moment(item.eventStartDate)
            const selectedStartDate = moment(this.dateRange.selectedStartDate)
            const selectedEndDate = moment(this.dateRange.selectedEndDate)
            const isItemStartDateInRange = itemStartDate.isSameOrAfter(selectedStartDate) && itemStartDate.isSameOrBefore(selectedEndDate)

            if (!isItemStartDateInRange) {
              return false
            }
          }
          return true
        })
      },

      filterEvents() {
        this.filteredEvents = this.getFilteredEvents(this.events)
        const calendarFilteredEvent = this.isDateRangeSelected ? this.getFilteredEvents(this.events, 'dateRange') : this.filteredEvents
        this.updateAvailableFilters()
        this.$emit('updateFilteredEvents', this.filteredEvents, calendarFilteredEvent)
      },

      typeIsDisabled(type) {
        return !this.availableTypes.some((item) => item.name === type)
      },

      fanExlusiveIsDisabled() {
        return !this.filteredEvents.some((item) => item.isFansOfMOExclusive)
      },
      complimentaryIsDisabled() {
        return !this.filteredEvents.some((item) => item.isComplimentary)
      },
    },
  }
</script>
